/**
 * Passo 1 do portão — o CPF, com o atalho do gov.br acima dele.
 *
 * O gov.br vem primeiro porque dispensa o código: deixá-lo abaixo do campo
 * faria a maioria digitar o CPF para só então descobrir o atalho. O CPF
 * confirmado pelo gov.br também precisa estar no cadastro do DTP (D-08, D-12).
 */

import { Info, Loader2, ShieldCheck } from 'lucide-react';
import { useId } from 'react';

import { BotaoGovBr } from '@/ui/BotaoGovBr';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import { mascaraCpf } from '../contrato';
import { AvisoDoPasso } from './AvisoDoPasso';
import { cpfEstaCompleto, type PropsDoPassoDaIdentidade } from './maquinaDeIdentidade';

export function IdentidadeCpf({ estado, despachar, acoes }: PropsDoPassoDaIdentidade) {
  const idDoCampo = useId();
  const idDaAjuda = `${idDoCampo}-ajuda`;
  const completo = cpfEstaCompleto(estado);

  return (
    <div className="space-y-5">
      <section
        aria-labelledby={`${idDoCampo}-atalho`}
        className="space-y-2 rounded-xl border border-primary/30 bg-primary/5 p-3"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3
            id={`${idDoCampo}-atalho`}
            className="text-xs font-semibold uppercase tracking-wide text-foreground"
          >
            Atalho recomendado
          </h3>
          <Badge variant="outline" className="border-primary/40">
            sem código
          </Badge>
        </div>
        <p className="text-xs leading-snug text-foreground">
          Pule a etapa do código de verificação — entre direto com a sua identidade digital gov.br.
        </p>
        <BotaoGovBr onClick={acoes.iniciarGovbr} disabled={estado.ocupado}>
          Entrar com gov.br
        </BotaoGovBr>
      </section>

      <div className="relative">
        <span aria-hidden="true" className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </span>
        <span className="relative flex justify-center text-[11px] uppercase">
          <span className="bg-card px-2 text-muted-foreground">ou use seu CPF</span>
        </span>
      </div>

      <form
        className="space-y-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (completo && !estado.ocupado) void acoes.continuarComCpf();
        }}
      >
        <div className="space-y-2">
          <Label htmlFor={idDoCampo}>CPF</Label>
          <Input
            id={idDoCampo}
            value={mascaraCpf(estado.cpf)}
            onChange={(evento) => despachar({ tipo: 'digitar_cpf', cpf: evento.target.value })}
            placeholder="000.000.000-00"
            inputMode="numeric"
            maxLength={14}
            autoComplete="username"
            aria-describedby={idDaAjuda}
          />
          <p id={idDaAjuda} className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Vamos localizar você no cadastro do DTP — no próximo passo você escolhe por onde receber
            o código.
          </p>
        </div>

        <AvisoDoPasso aviso={estado.aviso} />

        <Button type="submit" className="w-full" disabled={!completo || estado.ocupado}>
          {estado.ocupado ? (
            <Loader2 aria-hidden="true" className="animate-spin" />
          ) : (
            <ShieldCheck aria-hidden="true" />
          )}
          Continuar
        </Button>
      </form>

      <p className="text-xs text-muted-foreground">
        Não está no cadastro do DTP?{' '}
        <button
          type="button"
          onClick={() => despachar({ tipo: 'orientar', motivo: 'PROCURAR_DTP' })}
          className="rounded-sm text-link underline underline-offset-2 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
        >
          Veja o que fazer
        </button>
        .
      </p>
    </div>
  );
}

export default IdentidadeCpf;
